import type { LimbId } from "../combat/health.js";
import { getItemDef } from "../items/db.js";
import {
  ATTACH_KINDS,
  EQUIP_SLOTS,
  MAX_MOD_DEPTH,
  type EquipSlot,
  type ItemInstance,
} from "../items/types.js";
import { cloneGrid, createGrid, type GridState, type PlacedItem } from "./grid.js";

/**
 * What one PMC is carrying: seven equipment slots plus up to three grids —
 * `pockets` (always present, 4×1), `rig` and `backpack` (the capacity grid of
 * the WORN container, `null` when none). The container ITEM lives in
 * `equipment`, its CAPACITY here, for as long as it is worn; when it comes off
 * `applyInventoryOp` wraps the grid back onto the instance. One home at a time.
 *
 * CANONICAL SCAN ORDER — pockets, rig, backpack — is a determinism contract:
 * reload, pickup auto-placement and "find me this item" all walk it, on both
 * sides, and must agree.
 */

/** The grid areas that belong to the player. `"container"` (a world crate or a
 *  corpse) is deliberately NOT one of these — see `ops.ts`. */
export type GridAreaId = "pockets" | "rig" | "backpack";

/** THE canonical scan order. Never reorder; see the note above. */
export const GRID_AREAS: readonly GridAreaId[] = ["pockets", "rig", "backpack"] as const;

/** Pockets are fixed at 4x1 for every PMC in v1. */
export const POCKETS_W = 4;
export const POCKETS_H = 1;

export interface Inventory {
  equipment: Record<EquipSlot, ItemInstance | null>;
  /** Always 4x1, always present. */
  pockets: GridState;
  /** Capacity of the equipped rig, or `null` when nothing is worn there. */
  rig: GridState | null;
  /** Capacity of the equipped backpack, or `null`. */
  backpack: GridState | null;
}

/** An empty inventory: nothing worn, nothing carried but four empty pockets. */
export function createInventory(): Inventory {
  return {
    equipment: {
      pistol: null,
      weapon: null,
      weapon2: null,
      vest: null,
      helmet: null,
      rig: null,
      backpack: null,
    },
    pockets: createGrid(POCKETS_W, POCKETS_H),
    rig: null,
    backpack: null,
  };
}

/**
 * Deep-enough copy: fresh equipment record and grids, SHARED item instances
 * (immutable by convention), so a copy is O(items).
 */
export function cloneInventory(inv: Inventory): Inventory {
  return {
    equipment: {
      pistol: inv.equipment.pistol,
      weapon: inv.equipment.weapon,
      weapon2: inv.equipment.weapon2,
      vest: inv.equipment.vest,
      helmet: inv.equipment.helmet,
      rig: inv.equipment.rig,
      backpack: inv.equipment.backpack,
    },
    pockets: cloneGrid(inv.pockets),
    rig: inv.rig === null ? null : cloneGrid(inv.rig),
    backpack: inv.backpack === null ? null : cloneGrid(inv.backpack),
  };
}

/** The grid backing one area, or `null` when nothing is worn there. */
export function gridForArea(inv: Inventory, area: GridAreaId): GridState | null {
  return area === "pockets" ? inv.pockets : area === "rig" ? inv.rig : inv.backpack;
}

/**
 * Every grid the player owns, in CANONICAL SCAN ORDER, skipping absent ones —
 * the enumeration everything else is defined in terms of.
 */
export function inventoryGrids(inv: Inventory): { area: GridAreaId; grid: GridState }[] {
  const out: { area: GridAreaId; grid: GridState }[] = [];
  for (const area of GRID_AREAS) {
    const grid = gridForArea(inv, area);
    if (grid !== null) out.push({ area, grid });
  }
  return out;
}

/**
 * Locates an item in any of the player's GRIDS (never a slot). TOP-LEVEL ONLY:
 * a bandage in a medbox is stowed, not at hand. `findNestedItem`
 * (`inventory/nesting.ts`) searches the whole tree.
 */
export function findItemAnywhere(
  inv: Inventory,
  iid: string,
): { area: GridAreaId; placed: PlacedItem } | null {
  for (const { area, grid } of inventoryGrids(inv)) {
    for (const placed of grid.items) {
      if (placed.item.iid === iid) return { area, placed };
    }
  }
  return null;
}

/** Locates an item in an EQUIPMENT SLOT. The counterpart of `findItemAnywhere`. */
export function findEquipped(
  inv: Inventory,
  iid: string,
): { slot: EquipSlot; item: ItemInstance } | null {
  for (const slot of EQUIP_SLOTS) {
    const item = inv.equipment[slot];
    if (item !== null && item.iid === iid) return { slot, item };
  }
  return null;
}

/**
 * The armour protecting `limb`, or `null`. Fixed order — helmet, then vest — so
 * adding a face shield later is a content change.
 */
export function armorForLimb(inv: Inventory, limb: LimbId): ItemInstance | null {
  for (const slot of ["helmet", "vest"] as const) {
    const item = inv.equipment[slot];
    if (item === null) continue;
    const def = getItemDef(item.defId);
    if (def === null || def.kind !== "armor") continue;
    if (def.zones.includes(limb)) return item;
  }
  return null;
}

/**
 * Everything the player is carrying, in kilograms: worn equipment, what is
 * bolted to it, the rounds in every magazine (fitted or spare — the only place
 * rounds live), and every stack in every grid. Fitted attachments are walked
 * recursively to `MAX_MOD_DEPTH` in `ATTACH_KINDS` order at every level, and
 * nested contents in ARRAY order, each in full before the next — a fixed walk
 * order so the float addition is identical however the JSON arrived. A box you
 * carry weighs what is in it; anything else would be the one exploit
 * encumbrance exists to prevent.
 */
export function totalWeight(inv: Inventory): number {
  let kg = 0;

  /**
   * The cartridges an instance is carrying, in kilograms. No kind test: a
   * legacy instance with a `mag` key is weighed rather than ignored.
   */
  const roundsKg = (item: ItemInstance): number => {
    const rounds = item.mag ?? 0;
    if (rounds <= 0 || item.ammoDefId === undefined) return 0;
    const ammoDef = getItemDef(item.ammoDefId);
    return ammoDef !== null && ammoDef.kind === "ammo" ? ammoDef.weightKg * rounds : 0;
  };

  /**
   * Everything screwed to `item`, and everything screwed to that. `depth` is
   * `item`'s own depth, so the walk stops before `MAX_MOD_DEPTH`.
   */
  const addMods = (item: ItemInstance, depth: number): void => {
    const mods = item.mods;
    if (mods === undefined || depth >= MAX_MOD_DEPTH) return;
    for (const slotId of ATTACH_KINDS) {
      const mod = mods[slotId];
      if (mod === undefined) continue;
      const modDef = getItemDef(mod.defId);
      if (modDef === null) continue;
      // The part AND what is in it: a fitted magazine's rounds are the one
      // thing hanging off a gun that has a weight of its own.
      kg += modDef.weightKg * mod.stack;
      kg += roundsKg(mod);
      addMods(mod, depth + 1);
    }
  };

  const addInstance = (item: ItemInstance): void => {
    const def = getItemDef(item.defId);
    if (def === null) return;
    kg += def.weightKg * item.stack;
    kg += roundsKg(item);
    addMods(item, 0);
    // Bounded by `MAX_GRID_DEPTH` in any structure `applyInventoryOp` produced;
    // a def whose instance is not a container simply has no contents key.
    const contents = item.contents;
    if (contents === undefined) return;
    for (const p of contents.items) addInstance(p.item);
  };

  for (const slot of EQUIP_SLOTS) {
    const item = inv.equipment[slot];
    if (item !== null) addInstance(item);
  }
  for (const { grid } of inventoryGrids(inv)) {
    for (const p of grid.items) addInstance(p.item);
  }
  return kg;
}
